import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import s from './Navbar.module.css';
import { selectActivePage } from '../../../store/selectors/pageSelectors';
import { setActivePage, setPricingModal } from '../../../store/slices/pageSlice';
import { RootState, useAppDispatch } from '../../../store/store';
import IconDashboard from '../icons/IconDashboard';
import { createProject } from '../../api/textProjectApi';
import { createImageProject } from '../../api/imageProjectApi';
import IconUserCircle from '../icons/IconUserCircle';
import IconShareBare from '../icons/IconShareBare';
import IconBBQ from '../icons/IconBBQ';
import { clearGuard } from '../../../store/slices/navigationGuardSlice';
import { navigationHandlerRef } from '../../../store/ref/navigationHandlerRef';
import { toast } from 'react-toastify';
import IconSettings from '../icons/IconSettings';
import IconCalendar from '../icons/IconCalendar';
import IconEdit from '../icons/IconEdit';
import IconLogo from '../icons/IconLogo';
import { selectFirstConnectedSocial, selectUser } from '../../../store/selectors/authSelectors';
import { setSelectedSocialMedia } from '../../../store/slices/projectSlice';
import { selectSelectedSocialMedia } from '../../../store/selectors/projectSelectors';
import IconLock from '../icons/IconLock';
import { selectIsFreeSubscription } from '../../../store/selectors/subscriptionSelector';
const Navbar = () => {
    const dispatch = useAppDispatch();
    const activePage = useSelector(selectActivePage);
    const location = useLocation();
    const navigate = useNavigate();
    const guard = useSelector((state: RootState) => state.navigationGuard);
    const user = useSelector(selectUser);
    const [isOpen, setIsOpen] = useState(false);
    const firstConnectedSocial = useSelector(selectFirstConnectedSocial);
    const isFreeSubscription = useSelector(selectIsFreeSubscription);

    const selectedSocialMedia = useSelector(selectSelectedSocialMedia);

    useEffect(() => {
        console.log('firstConnectedSocial', firstConnectedSocial);
        if (firstConnectedSocial) {
            dispatch(setSelectedSocialMedia(firstConnectedSocial));
        } else {
            dispatch(setSelectedSocialMedia(null));
        }
    }, [firstConnectedSocial, dispatch, user?.social_connections]);

    useEffect(() => {
        return () => {
            dispatch(setSelectedSocialMedia(null));
        };
    }, []);

    useEffect(() => {
        let path = location.pathname.split('/')[1];
        if (path === 'image_project') {
            path = 'create_image_project';
        }
        if (path === 'text_project') {
            path = 'create_project';
        }
        dispatch(setActivePage(path));
    }, [location.pathname, dispatch]);

    const navItems = [
        {
            id: 'dashboard',
            icon: (
                <IconDashboard
                    width={22}
                    height={22}
                    color={activePage === 'dashboard' ? 'black' : '#7f7f7f'}
                />
            ),
            label: 'Dashboard',
            disabled: false,
        },
        {
            id: 'create_project',
            icon: isFreeSubscription ? (
                <IconLock
                    width={15}
                    height={22}
                    color={activePage === 'create_project' ? 'black' : '#7f7f7f'}
                />
            ) : (
                <IconEdit
                    width={22}
                    height={22}
                    color={activePage === 'create_project' ? 'black' : '#7f7f7f'}
                />
            ),
            label: 'Create Post',
            disabled: false,
        },
        {
            id: 'schedule',
            icon: (
                <IconCalendar
                    width={22}
                    height={22}
                    color={activePage === 'schedule' ? 'black' : '#7f7f7f'}
                />
            ),
            label: 'Schedule',
            disabled: false,
        },
        {
            id: 'settings',
            icon: (
                <IconSettings
                    width={22}
                    height={22}
                    color={activePage === 'settings' ? 'black' : '#7f7f7f'}
                />
            ),
            label: 'Settings',
            disabled: false,
        },
    ];

    const toggleMenu = () => setIsOpen((prev) => !prev);

    const handleClick = async (pageId: string) => {
        if (isFreeSubscription && pageId === 'create_project') {
            dispatch(setPricingModal(true));
            return;
        }

        setIsOpen(false);
        dispatch(setActivePage(pageId));
        if (guard.shouldBlock && navigationHandlerRef?.current) {
            await navigationHandlerRef.current();
            dispatch(clearGuard());
        }

        if (pageId === 'create_project' || pageId === 'create_image_project') {
            await handleCreateProject(pageId);
            return;
        }

        navigate(`/${pageId}`);
    };

    const handleCreateProject = async (pageId: string) => {
        try {
            const resultAction = await dispatch(
                createProject({
                    title: 'Empty Blank Project',
                    kind: 'text_only',
                    prompt: '',
                    category: 'myself',
                    industry: ['forest'],
                    content_type: selectedSocialMedia || 'threads',
                    tone: [],
                }),
            );
            if (createProject.fulfilled.match(resultAction)) {
                const projectId = resultAction.payload.id;
                if (pageId === 'create_project') {
                    await dispatch(
                        createImageProject({
                            title: 'Empty Blank Project',
                            slideshow: {
                                enabled: false,
                                count: 1,
                            },
                            projectId: projectId,
                        }),
                    ).unwrap();
                    navigate(`/create-post/${projectId}`);
                } else {
                    console.error('Failed to create project');
                }
            }
        } catch (error: any) {
            toast.error(error?.message || 'Error creating project');
            console.error('Error creating project:', error);
        }
    };

    return (
        <>
            {/* Top bar for mobile */}
            <div className={s.mobile_header}>
                <div className={s.hamburger} onClick={toggleMenu}>
                    <div className={s.bar}></div>
                    <div className={s.bar}></div>
                    <div className={s.bar}></div>
                </div>
                <div className={s.mobile_actions}>
                    <div className={s.icon_box}>
                        <IconBBQ />
                    </div>
                    <div className={s.icon_box}>
                        <IconShareBare />
                    </div>
                    <div className={s.icon_box}>
                        <IconUserCircle />
                    </div>
                </div>
            </div>

            {/* Sidebar (desktop or drawer) */}
            <div className={`${s.container} ${isOpen ? s.open : ''}`}>
                <div className={s.logo_container}>
                    <IconLogo />
                    <p>SimpleeCreate</p>
                </div>
                {navItems.map((item) => (
                    <div
                        key={item.id}
                        className={`${s.nav_item} ${activePage === item.id ? s.active : ''}`}
                        onClick={() => handleClick(item.id)}
                    >
                        <div className={`${s.icon} `}>{item.icon}</div>
                        <p className={`${s.text} ${activePage === item.id ? s.active_text : ''}`}>
                            {item.label}
                        </p>
                    </div>
                ))}
            </div>

            {/* Overlay on mobile when menu is open */}
            {isOpen && <div className={s.overlay} onClick={toggleMenu}></div>}
        </>
    );
};

export default Navbar;
